import React from 'react';
import { useReactToPrint } from 'react-to-print';
import dayjs, { Dayjs } from 'dayjs';
import { css } from '@emotion/react';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from '@mui/material';
import * as Highcharts from 'highcharts';
import HeatmapModule from 'highcharts/modules/heatmap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { color } from '@/styles';
import { DateSelector } from '@/components/mui/dateSelector';
import { OutlineButton } from '@/components/outlineButton';
import { useSnackbar, SnackbarType } from '@/contexts/SnackbarContext';
import { useLoading } from '@/contexts/LoadingContext';
import { useFetchResident } from '@/hooks/api/resident/useFetchResident';
import { currentNursingHomeSelector } from '@/redux/config/currentNursingHome';
import { SensorTrendHeatmapGraph } from './sensorTrendHeatmap';
import { TemperatureTrendHeatmapGraph } from './temperatureTrendHeatmap';
import { HumidityTrendHeatmapGraph } from './humidityTrendHeatmap';
import { ActivityTrendHeatmapGraph } from './activityTrendHeatmap';
import { NotifyAndCareRecordLabel } from './NotifyAndCareRecordLabel';
import { HighRiskTimesDisplay} from './highRiskTimesDisplay'
import { useFetchBenGraphData, BenGraphFetchFailedReason } from './useFetchBenGraphData';

HeatmapModule(Highcharts);

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface Props {
  nursingHomeId: string;
  residentId: string;
}

const FlexCenter = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PrintHidden = css`
  @media print {
    display: none;
  }
`;

const PrintContainer = css`
  @media print {
    width: 1440px;
  }
`;

const DateSelectContainer = css`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DataSelector = css`
  margin: 6px 0;
  display: grid;
  grid-template-columns: auto 1fr; /* 最大内容幅に基づいて列幅を設定 */
  align-items: center;
  gap: 16px;
`;

const UpperContainer = css`
  padding: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.17);
`;

const MiddleContainer = css`
  padding-bottom: 24px;

  @page {
    size: A4;
    margin: 4mm;
    padding: 4mm;
  }

  @media print {
    body {
      width: 1280px;
    }
  }
`;

const GraphContainer = css`
  margin-left: 32px;
  margin-right: 32px;
`;

const LabelText = css`
  text-align: end;
  min-width: 72px;
`;

const ContentStyle = css`
  display: flex;
  justify-content: space-between;
  flex: 1;
  margin-left: 16px;
`;

const Caution = css`
  ${FlexCenter};
  width: 100%;
  height: 120px;
`;

const HeatmapContainer = css`
  break-inside: avoid;
  margin-bottom: 48px;
`;

const ReportHeaderWrapperStyle = css`
  margin-bottom: 16px;
  padding: 8px;
  background-color: ${color.green};
  color: ${color.white};
`;

const ReportHeaderContainerStyle = css`
  display: flex;
  width: 100%;
  align-items: center;
`;

const ReportTitleContainerStyle = (isCompact: boolean) => css`
  flex: ${isCompact ? 1 : 1.4};
`;

const ReportTitleInnerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-right: 20px;
`;

const ReportInfoContainerStyle = css`
  flex: 1;
  text-align: left;

  & p {
    margin: 4px 0;
    font-size: 14px;
    font-weight: bold;
  }
`;

const FormControlStyle = css`
  padding: 0;
  min-width: 80px;
`;

const SelectMarginTop = css`
  margin-top: 6px;
`;

const UpdateButton = css`
    border-radius: 24px;
    padding: 8px 24px;
`;

export const ResidentDetailBenTrendAnalysis: React.FC<Props> = React.memo(({ residentId }) => {
  const { t, i18n } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { showLoading, hideLoading } = useLoading();
  const { resident } = useFetchResident(true, +residentId);
  const currentNursingHome = useSelector(currentNursingHomeSelector);
  const componentRef = React.useRef<HTMLDivElement>(null);

  // TODO: テスト用のref。あとで整理する
  const heatmapRef = React.useRef<HTMLDivElement>(null);
  const reportHeaderRef = React.useRef<HTMLDivElement>(null);

  // 自由設定フォームの時間帯プルダウンデータ
  const times = Array.from({ length: 24 }, (_, i) => `${i}:00`);
  // 日付指定フォームの日時
  const [startDate, setStartDate] = React.useState<Dayjs>(dayjs().subtract(2, 'week').startOf('day').utc());
  const [endDate, setEndDate] = React.useState<Dayjs>(dayjs().startOf('day').utc());
  // 時間帯ラジオボタンの値（timeRangeFormArray参照）
  const [timeRange, setTimeRange] = React.useState<string>('all-time');
  // 時間帯の自由設定フォームの時間
  const [selectedStartTime, setSelectedStartTime] = React.useState<string>('7:00');
  const [selectedEndTime, setSelectedEndTime] = React.useState<string>('19:00');
  // グラフの表示フラグ
  const [showNotificationPlot, setShowNotificationPlot] = React.useState<boolean>(true);
  const [showCareRecordPlot, setShowCareRecordPlot] = React.useState<boolean>(true);

  const {benGraphData, fetchBenGraphData} = useFetchBenGraphData();

  const [isPrinting, setIsPrinting] = React.useState(false);

  const timeRangeFormArray = [
    { key: 'all-time', value: t('allday', '終日') },
    { key: 'day-time', value: t('daytime7-19', '日中(7時~19時)') },
    { key: 'night-time', value: t('nighttime19-7', '夜間(19時~7時)') },
    { key: 'selected-time', value: t('specify', '自由設定') },
  ];

  const fetchFailedSnackBars: Record<BenGraphFetchFailedReason, {text: string, type?: SnackbarType}>= {
    TOO_LONG_PERIOD: {
      text: t('SpecifiedPeriodIsTooLong-PleaseNarrowDownThePeriodAndTryAgain',
          '選択期間が長すぎます。期間を絞ってやり直してください。'),
      type: 'error'
    },
    NO_DATA: {
      text: t('dataForTheSpecifiedPeriodWasNotFound',
          '指定した期間のデータが見つかりませんでした'),
    },
    UNKNOWN_ERROR: {
      text: t('AnErrorOccurred',
          'エラーが発生しました'),
      type: 'error'
    },
  }

  React.useEffect(() => {
    void handleUpdateButtonClick();
  }, []);

  const handleChangeStartDate = React.useCallback(
    (start: dayjs.Dayjs) => {
      setStartDate(start);
    },
    [startDate]
  );

  const handleChangeEndDate = React.useCallback(
    (end: dayjs.Dayjs) => {
      setEndDate(end);
    },
    [endDate]
  );

  const handleUpdateButtonClick = React.useCallback(async () => {

    const showFailedReason = (reason: BenGraphFetchFailedReason): void => {
      const config = fetchFailedSnackBars[reason];

      config.type? showSnackbar(config.text, config.type) : showSnackbar(config.text);
    }

    showLoading();

    const result = await fetchBenGraphData(
        {startDate,
        endDate,
        residentId,
        timeRange,
        selectedStartTime,
        selectedEndTime}
    )

    hideLoading();

    if (!result.success) {
      showFailedReason(result.reason);
    }

  }, [currentNursingHome, timeRange, startDate, endDate, selectedStartTime, selectedEndTime]);

  const PrintButton = ({ disable }: { disable: boolean }) => {
    return (
      <OutlineButton disable={disable} onClick={handlePrint} icon={<img src='/images/icons/print.svg' alt={t('analysis', '履歴・分析')} />}>
        <span>{t('Print', '印刷')}</span>
      </OutlineButton>
    );
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onBeforeGetContent: () => {
      setIsPrinting(true);
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve();
        }, 1000);
      });
    },
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const ReportHeader = React.memo(
      ({isPrinting}: {isPrinting: boolean}) => {
    return (
        <div css={ReportHeaderWrapperStyle} ref={reportHeaderRef}>
          <div css={ReportHeaderContainerStyle}>
            <div css={ReportTitleContainerStyle(isPrinting)}>
              <div css={ReportTitleInnerStyle}>
                <h1>{t('@dFreeSkinCareTrendsReport', 'DFree 皮膚ケア傾向レポート')}</h1>
              </div>
            </div>
            <div css={ReportInfoContainerStyle}>
              <p>
                {t('facility/Unit', '施設/ユニット')}: {currentNursingHome.name}/{resident?.nursingHomeUnitName}
              </p>
              <p>
                {t('resident', '入居者')}: {resident?.lastName} {resident?.name}
                {i18n.language.startsWith('ja') ? `${resident?.name && !resident.name.match(/(さま|様)$/) ? 'さま' : ''}` : ''}
              </p>
              <p>
                {t('period', '期間')}: {startDate.tz('Asia/Tokyo').format('YYYY/MM/DD')} -{' '}
                {endDate.tz('Asia/Tokyo').format('YYYY/MM/DD')}（{benGraphData?.sensor.main.yCategories.length}
                {t('forDays', '日分')}）
              </p>
            </div>
          </div>
        </div>
    );
  });

  return (
    <>
      <div css={UpperContainer}>
        <div css={DataSelector}>
          <div css={LabelText}>{t('period', '期間')}:</div>
          <div css={ContentStyle}>
            <div css={DateSelectContainer}>
              <DateSelector newValue={startDate} maxDate={endDate} onChange={handleChangeStartDate} />
              〜
              <DateSelector
                newValue={endDate}
                minDate={startDate}
                maxDate={dayjs(new Date())}
                onChange={handleChangeEndDate}
              />
            </div>
            <div css={[FlexCenter, DateSelectContainer]}>
              <PrintButton disable={!benGraphData?.hasHeatmapData} />
              <Button css={UpdateButton} variant='contained' onClick={handleUpdateButtonClick}>
                {t('display', '表示する')}
              </Button>
            </div>
          </div>
        </div>
        <div css={DataSelector}>
          <div css={LabelText}>{t('timePeriod', '時間帯')}:</div>
          <div>
            <div css={DateSelectContainer}>
              <FormControl component='fieldset'>
                <RadioGroup
                  row={true}
                  aria-label='group1'
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  {timeRangeFormArray.map((v, index) => (
                    <FormControlLabel key={index} value={v.key} control={<Radio />} label={v.value} />
                  ))}
                </RadioGroup>
              </FormControl>
              <FormControl disabled={timeRange !== 'selected-time'} css={FormControlStyle}>
                <InputLabel>{t('startTime', '開始時間')}</InputLabel>
                <Select
                  css={SelectMarginTop}
                  variant='standard'
                  value={selectedStartTime}
                  onChange={(e) => setSelectedStartTime(e.target.value)}
                >
                  {times.map((time, i) => (
                    <MenuItem key={time} value={time} disabled={i === times.indexOf(selectedEndTime)}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              〜
              <FormControl disabled={timeRange !== 'selected-time'} css={FormControlStyle}>
                <InputLabel>{t('endTime', '終了時間')}</InputLabel>
                <Select
                  css={SelectMarginTop}
                  variant='standard'
                  value={selectedEndTime}
                  onChange={(e) => setSelectedEndTime(e.target.value)}
                >
                  {times.map((time, i) => (
                    <MenuItem key={time} value={time} disabled={i === times.indexOf(selectedStartTime)}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        </div>
        <div css={DataSelector}>
          <div css={LabelText}>{t('options', 'オプション')}:</div>
          <FormGroup row={true}>
            <FormControlLabel
              checked={showCareRecordPlot}
              onChange={(e: any) => setShowCareRecordPlot(e.target.checked)}
              control={<Checkbox />}
              label={t('careHistory', 'ケア履歴')}
            />
            <FormControlLabel
              checked={showNotificationPlot}
              onChange={(e: any) => setShowNotificationPlot(e.target.checked)}
              control={<Checkbox />}
              label={t('notification', '通知')}
            />
          </FormGroup>
        </div>
      </div>
      <div css={PrintContainer} ref={componentRef}>
        <div css={MiddleContainer}>
          {benGraphData?.hasHeatmapData ? (
            <>
              <ReportHeader isPrinting={isPrinting}/>
              <div css={GraphContainer} ref={heatmapRef}>
                <HighRiskTimesDisplay highRiskTimes={benGraphData.highRiskTimes} />
                <NotifyAndCareRecordLabel />
                <div css={HeatmapContainer}>
                  <SensorTrendHeatmapGraph
                    heatmapData={benGraphData.sensor}
                    careRecordPlot={benGraphData.careRecordPlot}
                    notificationPlot={benGraphData.notificationPlot}
                    showCareRecordPlot={showCareRecordPlot}
                    showNotificationPlot={showNotificationPlot}
                    isPrinting={isPrinting}
                  />
                </div>
                <div css={HeatmapContainer}>
                  <TemperatureTrendHeatmapGraph
                      heatmapData={benGraphData.temperature}
                      careRecordPlot={benGraphData.careRecordPlot}
                      notificationPlot={benGraphData.notificationPlot}
                      showCareRecordPlot={showCareRecordPlot}
                      showNotificationPlot={showNotificationPlot}
                      isPrinting={isPrinting}
                  />
                </div>
                <div css={HeatmapContainer}>
                  <HumidityTrendHeatmapGraph
                      heatmapData={benGraphData.humidity}
                      careRecordPlot={benGraphData.careRecordPlot}
                      notificationPlot={benGraphData.notificationPlot}
                      showCareRecordPlot={showCareRecordPlot}
                      showNotificationPlot={showNotificationPlot}
                      isPrinting={isPrinting}
                  />
                </div>
                <div css={HeatmapContainer}>
                  <ActivityTrendHeatmapGraph
                    heatmapData={benGraphData.activity}
                    careRecordPlot={benGraphData.careRecordPlot}
                    notificationPlot={benGraphData.notificationPlot}
                    positionChangedPlot={benGraphData.activityPositionChangedPlot}
                    showCareRecordPlot={showCareRecordPlot}
                    showNotificationPlot={showNotificationPlot}
                    isPrinting={isPrinting}
                  />
                </div>
              </div>
            </>
          ) : (
            <div css={[Caution, PrintHidden]}>{t('noData', 'データがありません')}</div>
          )}
        </div>
      </div>
    </>
  );
});
